<script setup lang="ts">
import { computed, onMounted, shallowRef } from 'vue';
import { useSocketStore } from '@/store/socket-store';
import { marketplaceService } from '@/connect/services/marketplace';
import { wait } from '@/modules/common/services/wait';
import { ConnectError } from '@connectrpc/connect';
import i18n from '@/localisation/i18n';

const emit = defineEmits<{
  (event: 'back' | 'close-modal' | 'success'): void;
}>();

const socketStore = useSocketStore();

type DialogState = 'loading' | 'confirm' | 'in_progress' | 'completed' | 'failed' | 'error';

const state = shallowRef<DialogState>('loading');
const activeOrderCount = shallowRef(0);

onMounted(async () => {
  try {
    const result = await marketplaceService.getActiveOrderCount();
    if (!result.success) {
      errorMessage.value = i18n.t(result.error, result.details) as string;
      state.value = 'error';
      return;
    }

    activeOrderCount.value = result.success ? result.data.count : 0;
    state.value = 'confirm';
  } catch (err) {
    errorMessage.value = ConnectError.from(err).message;
    state.value = 'error';
  }
});
const totalCount = shallowRef(0);
const cancelledCount = shallowRef(0);
const failedCount = shallowRef(0);
const errorMessage = shallowRef('');

const progressPercent = computed(() => {
  if (totalCount.value === 0) return 0;
  return Math.min(
    100,
    Math.round(((cancelledCount.value + failedCount.value) / totalCount.value) * 100)
  );
});

const summaryText = computed(() => {
  const parts: string[] = [];
  if (cancelledCount.value > 0) {
    parts.push(`${cancelledCount.value} cancelled`);
  }
  if (failedCount.value > 0) {
    parts.push(`${failedCount.value} failed`);
  }
  return parts.join(', ');
});

let minimumWait: Promise<void> | null = null;

async function applyFinalState(finalState: DialogState): Promise<void> {
  if (minimumWait) {
    await minimumWait;
  }
  state.value = finalState;
}

socketStore.useEventHandler('cancelAllOrdersProgress', (payload) => {
  totalCount.value = payload.totalCount;
  cancelledCount.value = payload.cancelledCount;
  failedCount.value = payload.failedCount;

  if (payload.status === 'completed' || payload.status === 'failed') {
    void applyFinalState(payload.status);
  }
});

async function onConfirm(): Promise<void> {
  state.value = 'in_progress';
  minimumWait = wait(1000);
  try {
    const result = await marketplaceService.cancelAllOrders();
    if (!result.success) {
      errorMessage.value = i18n.t(result.error, result.details) as string;
      state.value = 'error';
      return;
    }

    totalCount.value = result.data.totalCount;
    if (result.data.totalCount === 0) {
      await minimumWait;
      state.value = 'completed';
    }
  } catch (err) {
    errorMessage.value = ConnectError.from(err).message;
    await minimumWait;
    state.value = 'error';
  }
}

function onClose(): void {
  if (state.value === 'completed') {
    emit('success');
  }
  emit('close-modal');
}
</script>

<template>
  <v-dialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    max-width="500"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistent
    :value="true"
    @keydown.esc="onClose()"
    @shortkey.native="onClose()"
  >
    <v-card>
      <v-card-title>
        <span class="headline">Cancel All Orders</span>
      </v-card-title>

      <v-card-text>
        <!-- Loading state -->
        <template v-if="state === 'loading'">
          <div class="d-flex justify-center py-4">
            <v-progress-circular data-test="cancel-all-loading" indeterminate />
          </div>
        </template>

        <!-- Confirm state -->
        <template v-if="state === 'confirm'">
          <p data-test="cancel-all-description">
            You are about to cancel all
            <strong>{{ activeOrderCount }}</strong> active orders. This action cannot be undone.
          </p>
        </template>

        <!-- In Progress state -->
        <template v-if="state === 'in_progress'">
          <p>Cancelling orders...</p>
          <v-progress-linear
            color="primary"
            data-test="cancel-all-progress"
            height="20"
            rounded
            :value="progressPercent"
          >
            <strong>{{ progressPercent }}%</strong>
          </v-progress-linear>
          <p class="mt-2 text-caption">
            {{ cancelledCount + failedCount }} / {{ totalCount }} processed
          </p>
        </template>

        <!-- Completed state -->
        <template v-if="state === 'completed'">
          <v-alert
            data-test="cancel-all-success"
            dense
            :type="cancelledCount === 0 ? 'info' : failedCount === 0 ? 'success' : 'warning'"
          >
            <template v-if="cancelledCount === 0"> No active orders to cancel. </template>
            <template v-else-if="failedCount === 0">
              Successfully cancelled all {{ cancelledCount }} orders.
            </template>
            <template v-else> {{ summaryText }} out of {{ totalCount }} orders. </template>
          </v-alert>
        </template>

        <!-- Failed state -->
        <template v-if="state === 'failed'">
          <v-alert data-test="cancel-all-failed" dense type="error">
            Cancel all failed. {{ summaryText }} out of {{ totalCount }} orders.
          </v-alert>
        </template>

        <!-- API Error state -->
        <template v-if="state === 'error'">
          <v-alert data-test="cancel-all-error" dense type="error">
            {{ errorMessage }}
          </v-alert>
        </template>
      </v-card-text>

      <v-card-actions class="d-flex px-8 py-4">
        <template v-if="state === 'loading' || state === 'confirm'">
          <v-btn color="secondary" data-test="cancel-all-back-btn" @click="emit('back')">
            Back
          </v-btn>
          <v-spacer />
          <v-btn
            color="red"
            data-test="cancel-all-confirm-btn"
            :disabled="state === 'loading'"
            @click="onConfirm()"
          >
            Confirm
          </v-btn>
        </template>

        <template v-else-if="state === 'in_progress'">
          <v-spacer />
          <v-btn disabled> Please wait... </v-btn>
        </template>

        <template v-else>
          <v-spacer />
          <v-btn color="primary" data-test="cancel-all-close-btn" @click="onClose()"> Close </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
